/* global GM_STATE */
import { getChildStockStatus } from '@/components/helpers/bundle-items'
import { openModal } from '@/components/helpers/dom'
import {
  STORAGE_1P_KEY,
  STORAGE_2P_KEY,
  formatPreorderDate,
  getStockStatus,
  getMetafieldKey,
  parseStockJson,
  resolveActiveWarehouse
} from 'helpers/stock-status'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { addCartItem, getCart, getMaxQuantityByVariant } from 'libs/cart'
import { useEffect, useMemo, useState } from 'preact/hooks'
import ButtonATC from 'snippets/button-atc/button-atc'
import ButtonATCDisabled from 'snippets/button-atc/button-atc-disabled'
import Icon from 'snippets/icon/icon'
import ProductStatus from 'snippets/product-status/product-status'
import QuantityInput from 'snippets/quantity-input/quantity-input'

function ProductATC({
  currentVariant,
  initProduct,
  buttonText,
  buttonClass = '',
  buttonWrapperClass = 'mt-4 md:mt-6',
  showStatus = false,
  isAnchorSection = false,
  showQty = false
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [cartItems, setCartItems] = useState(GM_STATE.cart.initCart.items)
  const [maxQuantity, setMaxQuantity] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [reRenderKey, setReRenderKey] = useState(1)
  const [bundleAvailability, setBundleAvailability] = useState('available')
  const [bundlePreOrderDates, setBundlePreOrderDates] = useState([])
  const [warehouse1P, setWarehouse1P] = useState(() => localStorage.getItem(STORAGE_1P_KEY) || null)
  const [warehouse2P, setWarehouse2P] = useState(() => localStorage.getItem(STORAGE_2P_KEY) || null)
  const notifyMeWhenAvailable = GM_STATE.apps.backInStock.notifyMeWhenAvailable
  const klaviyoNotifyPopupId = GM_STATE.apps.backInStock.klaviyoNotifyPopupId

  // Resolve warehouse-based stock status
  const hasPostcodeInput = warehouse1P !== null || warehouse2P !== null
  const activeWarehouse = hasPostcodeInput
    ? resolveActiveWarehouse(warehouse1P, warehouse2P, currentVariant)
    : null
  const warehouseStockData = activeWarehouse
    ? parseStockJson(currentVariant.metafields?.[getMetafieldKey(activeWarehouse)])
    : null

  // When a postcode is set: use warehouse stockstatusmessage
  // When no postcode: isPreorder is false — show ATC or OOS based on Shopify availability
  const { isPreorder, isOutOfStock } = activeWarehouse
    ? getStockStatus(warehouseStockData, currentVariant, 0)
    : { isPreorder: false, isOutOfStock: false }
  const preOrderDate = activeWarehouse && warehouseStockData?.earliestpodate
    ? warehouseStockData.earliestpodate
    : ''

  const bundleComponentSKUs = initProduct?.metafields?.bundle_child_skus ? initProduct?.metafields?.bundle_child_skus.split(', ') : []
  const earliestPODate = useMemo(() => {
    if (bundlePreOrderDates.length <= 0 && preOrderDate) {
      return preOrderDate
    }

    const maxDate = new Date(Math.max(...bundlePreOrderDates.map(date => new Date(date))))

    const formatDate = maxDate.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })

    return formatDate
  }, [bundlePreOrderDates, preOrderDate])

  const bundleComposition = bundleComponentSKUs?.map((childItem) => {
    const bundleObj = {}
    bundleObj.sku = childItem.split('#')[0]
    bundleObj.qty = parseInt(childItem.split('#')[1])
    return bundleObj
  })

  const bundleCompositionMarker = bundleComposition.map((x) => x.sku + x.qty).join()

  useEffect(() => {
    const onPostcodeUpdated = (e) => {
      setWarehouse1P(e.detail.warehouse1P ?? null)
      setWarehouse2P(e.detail.warehouse2P ?? null)
    }
    window.addEventListener('postcode:updated', onPostcodeUpdated)
    return () => window.removeEventListener('postcode:updated', onPostcodeUpdated)
  }, [])

  useEffect(() => {
    if (quantity === undefined || quantity === null) {
      setQuantity(1)
    }
  }, [])

  useEffect(() => {
    const getBundleStockStatus = async (bundleComposition) => {
      if (!bundleComposition) {
        return []
      }
      const childBundleStockStatus = await getChildStockStatus(bundleComposition)
        .then((res) => {
          return res
        })
        .then((body) => {
          if (body.errors) {
            throw body
          }
          setBundleAvailability(body?.childrenBundleStatus)
          setBundlePreOrderDates(body.poDates || [])
          return body
        })
        .catch((err) => {
          console.error('Some error getting the bundle item status', err)
          return []
        })
      return childBundleStockStatus
    }
    getBundleStockStatus(bundleComposition)
  }, [bundleCompositionMarker])

  // Bundle is available
  const isRegularAvailableProduct = currentVariant.available && maxQuantity > 0
  const isAvailableBundle = bundleComponentSKUs.length > 0 && bundleAvailability === 'available'

  const { productPackage } = window.productState

  const resetQuantity = (value = 1) => {
    setQuantity(value)
    setReRenderKey(reRenderKey + 1)
  }

  const addToCart = async () => {
    setIsAdding(true)
    let properties = {}
    if (productPackage) {
      properties = {
        _package: productPackage
      }
    }

    if (isPreorder || bundleAvailability === 'Pre-order') {
      properties = {
        ...properties,
        Status: 'Preorder',
        _preorderDate: earliestPODate
      }
    }

    try {
      const cartSingle = await addCartItem(currentVariant.id, properties, quantity)

      if (cartSingle) {
        globalEmitter.emit(emitterProps.cart.add, cartSingle)
      }

      const max = getMaxQuantityByVariant(cartSingle.items, currentVariant)

      if (max > 0 && max < quantity) {
        resetQuantity(max)
      }

      if (max <= 0) {
        resetQuantity(1)
      }

      syncMaxQuantity()
    } catch (err) {
      console.error('Error adding to cart:', err)
    } finally {
      setIsAdding(false)
    }
  }

  const handleAddToCart = () => addToCart()

  const getMaxQuantity = async (variant) => {
    const cartData = await getCart()
    setCartItems(cartData.items)
    return getMaxQuantityByVariant(cartData.items, variant)
  }

  const syncMaxQuantity = async () => {
    setIsLoading(true)
    const maxValue = await getMaxQuantity(currentVariant)
    setMaxQuantity(maxValue)
    setIsLoading(false)
  }

  const updateMaxQuantity = (items) => {
    setIsLoading(true)
    const maxValue = getMaxQuantityByVariant(items, currentVariant)
    setCartItems(items)
    setMaxQuantity(maxValue)
    setIsLoading(false)
  }

  const cartUpdateListener = (items) => updateMaxQuantity(items, currentVariant)

  const openNotifyModal = () => {
    window._klOnsite = window._klOnsite || []
    window._klOnsite.push(['openForm', klaviyoNotifyPopupId])
  }

  useEffect(() => {
    if (currentVariant) {
      updateMaxQuantity(cartItems, currentVariant)
      globalEmitter.on(emitterProps.cart.update, cartUpdateListener)
    }
    return () => {
      globalEmitter.off(emitterProps.cart.update, cartUpdateListener)
    }
  }, [currentVariant])

  if (isLoading) {
    return (
      <div className={buttonWrapperClass}>
        <div className="mt-2 h-[56px] w-full rounded-full bg-grey-1000"></div>
        {showStatus && <ProductStatus maxQuantity={maxQuantity} isLoading={isLoading} initProduct={initProduct} />}
      </div>
    )
  }

  if (activeWarehouse && isOutOfStock) {
    return (
      <div className={buttonWrapperClass}>
        <div className="flex items-center gap-3">
          {!isAnchorSection && <ButtonATCDisabled text={translate.product.outStock} />}
        </div>
        {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
          <div className="pt-6" data-shopassistant-container></div>
        )}
        {showStatus && (
          <ProductStatus
            maxQuantity={maxQuantity}
            isLoading={isLoading}
            initProduct={initProduct}
            isAvailable={false}
          />
        )}
      </div>
    )
  }

  if (isPreorder || bundleAvailability === 'Pre-order') {
    return (
      <div className={buttonWrapperClass}>
        <div className="flex items-center gap-3">
          <QuantityInput
            inputKey={`quickview-${currentVariant?.id || 'variant'}`}
            quantity={quantity ?? 1}
            min={1}
            max={maxQuantity || currentVariant?.inventory_quantity || 999}
            setQuantity={setQuantity}
            classWrap={`w-[109px] rounded-full h-12 ${showQty ? '' : 'hidden'}`}
            classButton="w-11 h-full p-0"
            iconClass="text-black"
          />
          <ButtonATC
            text="Pre-order"
            onHandle={handleAddToCart}
            loading={isAdding}
            buttonClass={buttonClass}
          />
        </div>
        <div className="mt-4">
          {!isAnchorSection && (
            <button
              className="button-outlined product-preorder-button flex w-full flex-row items-center justify-center gap-1.5 tracking-[0.84px] normal-case text-sm"
              onClick={() => openModal('modal-preorder')}
              title={window.translate.product.preOrderButton}
            >
              <Icon viewBox="0 0 20 20" name="icon-question" className="w-5 h-5 text-grey-500" />
              {window.translate.product.preOrderButton}
            </button>
          )}
        </div>
        {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
          <div className="pt-6" data-shopassistant-container></div>
        )}
      </div>
    )
  }

  let isDisabled = false

  if (bundleComponentSKUs.length > 0) {
    isDisabled = !isRegularAvailableProduct || !isAvailableBundle
  } else {
    isDisabled = !isRegularAvailableProduct
  }

  if (isRegularAvailableProduct || isAvailableBundle) {
    return (
      <div className={buttonWrapperClass}>
        <div className="flex items-center gap-3">
          {isDisabled
            ? <ButtonATCDisabled text={'Out Of Stock'} />
            : <>
              <QuantityInput
                inputKey={`quickview-${currentVariant?.id || 'variant'}`}
                quantity={quantity ?? 1}
                min={1}
                max={maxQuantity || currentVariant?.inventory_quantity || 999}
                setQuantity={setQuantity}
                classWrap={`w-[122px] rounded-full h-12 ${showQty ? '' : 'hidden'}`}
                classButton="w-11 h-full p-0"
                iconClass="text-black"
              />
              <ButtonATC
                disabled={isDisabled}
                text={buttonText}
                onHandle={handleAddToCart}
                loading={isAdding}
                buttonClass={buttonClass}
              />
            </>}
        </div>
        {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
          <div className="pt-6" data-shopassistant-container></div>
        )}
        {showStatus && (
          <ProductStatus
            maxQuantity={maxQuantity}
            isLoading={isLoading}
            initProduct={initProduct}
            isAvailable={true}
          />
        )}
      </div>
    )
  }

  // Regular ATC
  return (
    <div className={buttonWrapperClass}>
      <div className="flex items-center gap-3">
        {!isAnchorSection && <ButtonATCDisabled text={translate.product.outStock} />}
      </div>
      {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
        <div className="pt-6" data-shopassistant-container></div>
      )}
      {showStatus && (
        <ProductStatus
          maxQuantity={maxQuantity}
          isLoading={isLoading}
          initProduct={initProduct}
          isAvailable={false}
        />
      )}
    </div>
  )
}

export default ProductATC
